import React, { useCallback, useState } from "react";
import { Linking, Pressable, StyleSheet, View } from "react-native";
import { ArrowSquareOutIcon, CaretDownIcon, CaretRightIcon, LinkIcon, ShieldIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type {
  ActivityDetail,
  CourseEvaluatorReviewSidebarItem,
  PublishManualStep,
  StructuredFieldEdits,
  StructuredFields,
} from "../types/reviewTypes";
import ActivityPanelTabs from "./ActivityPanelTabs";
import IssueFindingsList from "./IssueFindingsList";
import SegmentedDiffView from "./SegmentedDiffView";

interface ExternalLinkPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetail: ActivityDetail | null;
  manualSteps?: PublishManualStep[];
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFieldEdits) => void;
}

// ---------------------------------------------------------------------------
// Quality indicator presentation
// ---------------------------------------------------------------------------

function getQualityPresentation(qualityLabel: string | undefined) {
  switch (qualityLabel?.toLowerCase()) {
    case "excellent":
      return { label: "Excellent", bgColor: "#E3FBCC", textColor: "#2B5314" };
    case "good":
      return { label: "Good", bgColor: "#CCD7FF", textColor: "#000B33" };
    case "fair":
      return { label: "Fair", bgColor: "#FEF3C7", textColor: "#D97706" };
    case "poor":
    case "needs_revision":
    case "needs revision":
      return { label: "Needs Revision", bgColor: colors.error[50], textColor: colors.error[700] };
    default:
      return null;
  }
}

function getSubtypePresentation(category: string | undefined) {
  switch (category) {
    case "lecture":
      return { label: "Lecture", bgColor: "#F5F0FE", textColor: "#7244BB" };
    case "supplementary":
      return { label: "Supplementary", bgColor: "#F2F4F7", textColor: "#344054" };
    case "instructional":
    case "instructional-content":
      return { label: "Instructional", bgColor: "#E6F4FF", textColor: "#2563EB" };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Current URL card
// ---------------------------------------------------------------------------

interface CurrentUrlCardProps {
  url: string;
}

function CurrentUrlCard({ url }: CurrentUrlCardProps) {
  const handlePress = useCallback(() => {
    Linking.openURL(url).catch(() => {
      // URL could not be opened — silently ignore on unsupported platforms
    });
  }, [url]);

  return (
    <View style={urlCardStyles.container}>
      <CustomText text="CURRENT URL" useTranslationText={false} size="xs" weight="bold" style={urlCardStyles.label} />
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={`Open external link: ${url}`}
        onPress={handlePress}
        style={urlCardStyles.linkRow}>
        <ArrowSquareOutIcon size={14} color={colors.brand[600]} weight="regular" />
        <CustomText
          text={url}
          useTranslationText={false}
          size="s"
          weight="medium"
          style={urlCardStyles.linkText}
          numberOfLines={2}
        />
      </Pressable>
    </View>
  );
}

const urlCardStyles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 8,
  },
  label: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  linkRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  linkText: {
    color: colors.brand[600],
    flex: 1,
    fontFamily: "monospace",
  },
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function ExternalLinkPanel({
  selectedReviewItem,
  activityDetail,
  manualSteps,
  isExpanded,
  isEditDisabled,
  onSaveEdit,
}: ExternalLinkPanelProps) {
  const detail = activityDetail;
  const qualityPresentation = detail?.qualityLabel ? getQualityPresentation(detail.qualityLabel) : null;
  const subtypePresentation = getSubtypePresentation(detail?.contentCategory);
  const hasIssueFindings = Boolean(detail?.issueFindings?.length);
  const [isWhyCardCollapsed, setIsWhyCardCollapsed] = useState(false);

  const currentFields: StructuredFields = {
    title: detail?.currentTitle ?? selectedReviewItem.title,
    description: detail?.currentDescription ?? "",
    url: detail?.url,
  };

  const proposedFields: StructuredFields = {
    title: detail?.recommendedTitle ?? detail?.currentTitle ?? selectedReviewItem.title,
    description: detail?.recommendedDescription ?? "",
    url: detail?.url,
  };

  const hasDiff =
    Boolean(proposedFields.description) ||
    (proposedFields.title !== currentFields.title && Boolean(proposedFields.title));

  return (
    <View style={styles.panel}>
      {!isExpanded && (
        <>
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <View style={styles.typeBadge}>
                <LinkIcon size={14} color={colors.grey[700]} weight="bold" />
                <CustomText
                  text="External Link"
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.typeBadgeText}
                />
              </View>
              {qualityPresentation && (
                <View style={[styles.qualityBadge, { backgroundColor: qualityPresentation.bgColor }]}>
                  <CustomText
                    text={qualityPresentation.label}
                    useTranslationText={false}
                    size="xs"
                    weight="medium"
                    style={{ color: qualityPresentation.textColor }}
                  />
                </View>
              )}
            </View>
            {subtypePresentation && (
              <View style={[styles.subtypeBadge, { backgroundColor: subtypePresentation.bgColor }]}>
                <CustomText
                  text={subtypePresentation.label}
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={{ color: subtypePresentation.textColor }}
                />
              </View>
            )}
          </View>

          <CustomText
            text={detail?.currentTitle ?? selectedReviewItem.title}
            useTranslationText={false}
            size="xl"
            weight="bold"
            style={styles.title}
          />
        </>
      )}

      <ActivityPanelTabs attachments={detail?.attachments} manualSteps={manualSteps}>
        <View style={styles.reviewContent}>
          {/* Why recommendation details */}
          {!isExpanded && hasIssueFindings && (
            <View style={styles.whyCard}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Toggle why recommendation details"
                onPress={() => setIsWhyCardCollapsed(!isWhyCardCollapsed)}
                style={styles.whyHeaderRow}>
                <CustomText
                  text="Why we're recommending this"
                  useTranslationText={false}
                  size="s"
                  weight="bold"
                  style={styles.whyHeading}
                />
                {isWhyCardCollapsed ? (
                  <CaretRightIcon size={16} color={colors.grey[600]} weight="bold" />
                ) : (
                  <CaretDownIcon size={16} color={colors.grey[600]} weight="bold" />
                )}
              </Pressable>
              {!isWhyCardCollapsed && (
                <>
                  <IssueFindingsList findings={detail?.issueFindingsDetailed} />
                  {detail?.rubricCode && (
                    <View style={styles.rubricRow}>
                      <ShieldIcon size={12} color={colors.brand[600]} weight="fill" />
                      <CustomText
                        text={`${detail.rubricCode}: ${detail.rubricTitle}`}
                        useTranslationText={false}
                        size="xs"
                        weight="medium"
                        style={styles.rubricLink}
                      />
                      {detail.rubricDescription && (
                        <CustomText
                          text={` \u2014 ${detail.rubricDescription}`}
                          useTranslationText={false}
                          size="xs"
                          style={styles.rubricDescription}
                        />
                      )}
                    </View>
                  )}
                </>
              )}
            </View>
          )}

          {/* Current URL */}
          {detail?.url && <CurrentUrlCard url={detail.url} />}

          {/* Title + Description diff (both editable) */}
          {hasDiff && (
            <SegmentedDiffView
              variant="recommendation"
              currentFields={currentFields}
              proposedFields={proposedFields}
              isExpanded={isExpanded}
              isEditDisabled={isEditDisabled}
              onSaveEdit={onSaveEdit}
            />
          )}

          {/* Last modified */}
          {detail?.lastModifiedBy && (
            <CustomText
              text={`Last modified by ${detail.lastModifiedBy} on ${detail.lastModifiedAt ?? "unknown date"}`}
              useTranslationText={false}
              size="xs"
              style={styles.lastModified}
            />
          )}
        </View>
      </ActivityPanelTabs>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    height: 24,
    borderRadius: 8,
    backgroundColor: colors.grey[100],
  },
  typeBadgeText: {
    color: colors.grey[700],
  },
  qualityBadge: {
    paddingHorizontal: 10,
    height: 24,
    justifyContent: "center",
    borderRadius: 9999,
  },
  subtypeBadge: {
    paddingHorizontal: 8,
    height: 24,
    justifyContent: "center",
    borderRadius: 8,
  },
  title: {
    color: colors.grey[900],
  },
  reviewContent: {
    gap: 16,
  },
  // --- Why card ---
  whyCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 6,
  },
  whyHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 8,
  },
  whyHeading: {
    color: colors.grey[900],
    marginBottom: 0,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  rubricLink: {
    color: colors.brand[600],
  },
  rubricDescription: {
    color: colors.grey[500],
  },
  lastModified: {
    color: colors.grey[400],
  },
});

export default ExternalLinkPanel;
